import { doc } from "firebase/firestore";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorText, FieldLabel, LoadingView, PrimaryButton, SBCard, SecondaryButton } from "@/components/ui";
import { db } from "@/firebase/app";
import { useDoc } from "@/hooks/firestoreHooks";
import { parseLeague } from "@/models/catalog";
import {
  createInvite,
  INVITE_ROLE_LABELS,
  inviteTargetDisplayName,
  listInvites,
  resendInvite,
  revokeInvite,
  type SBotInviteRole,
  type SBotInviteSummary,
} from "@/services/invites";
import { useActiveLeagueID, useLeagueSnapshot } from "@/features/home/useLeagueSnapshot";
import "@/features/home/home.css";
import "@/features/league/league.css";

// Port of LeagueInvitationsView — invite creation + management through the
// existing /invites function endpoints.

export function LeagueInvitations() {
  const { currentUser } = useAuth();
  const { leagueID } = useActiveLeagueID();
  const league = useDoc(leagueID ? doc(db, "leagues", leagueID) : null, parseLeague);
  const snapshot = useLeagueSnapshot(false);

  const [invites, setInvites] = useState<SBotInviteSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [inviteeEmail, setInviteeEmail] = useState("");
  const [role, setRole] = useState<SBotInviteRole>("athlete");
  const [organizationID, setOrganizationID] = useState("");
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [confirmation, setConfirmation] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!leagueID) {
      setInvites([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      setInvites(await listInvites(leagueID));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invites could not be loaded.");
    } finally {
      setIsLoading(false);
    }
  }, [leagueID]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const needsOrganization =
    role === "organizationAdmin" || role === "organizationManager" || role === "organizationStaff" || role === "athlete";
  const selectedOrganization = useMemo(
    () => snapshot.organizations.find((org) => org.id === organizationID),
    [snapshot.organizations, organizationID],
  );

  const send = async () => {
    if (!currentUser || !league.data) return;
    setIsSending(true);
    setError(null);
    setConfirmation(null);
    try {
      await createInvite({
        inviteeEmail: inviteeEmail.trim().toLowerCase(),
        role,
        leagueID: league.data.id,
        leagueName: league.data.name,
        organizationID: needsOrganization && selectedOrganization ? selectedOrganization.id : undefined,
        organizationName: needsOrganization && selectedOrganization ? selectedOrganization.name : undefined,
        message: message.trim() || undefined,
        inviterFirstName: currentUser.firstName,
        inviterLastName: currentUser.lastName,
        inviterEmail: currentUser.email,
      });
      setConfirmation(`Invite sent to ${inviteeEmail.trim()}.`);
      setInviteeEmail("");
      setMessage("");
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invite could not be sent.");
    } finally {
      setIsSending(false);
    }
  };

  const act = async (action: () => Promise<void>) => {
    setError(null);
    try {
      await action();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Action failed.");
    }
  };

  return (
    <div className="list-screen league-screen">
      <div className="list-screen-head">
        <h1>Invitations</h1>
      </div>

      <SBCard className="home-card">
        <h3>Send an invite</h3>
        <FieldLabel label="Invitee email">
          <input type="email" value={inviteeEmail} onChange={(e) => setInviteeEmail(e.target.value)} placeholder="athlete@example.com" />
        </FieldLabel>
        <FieldLabel label="Role">
          <select value={role} onChange={(e) => setRole(e.target.value as SBotInviteRole)}>
            {(Object.keys(INVITE_ROLE_LABELS) as SBotInviteRole[]).map((value) => (
              <option key={value} value={value}>
                {INVITE_ROLE_LABELS[value]}
              </option>
            ))}
          </select>
        </FieldLabel>
        {needsOrganization && (
          <FieldLabel label="Organization">
            <select value={organizationID} onChange={(e) => setOrganizationID(e.target.value)}>
              <option value="">— League-wide —</option>
              {snapshot.organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </FieldLabel>
        )}
        <FieldLabel label="Message (optional)">
          <textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} />
        </FieldLabel>
        <PrimaryButton
          title={isSending ? "Sending…" : "Send invite"}
          onClick={() => void send()}
          disabled={isSending || !inviteeEmail.trim() || !league.data}
        />
        {confirmation && <p className="home-hint">{confirmation}</p>}
      </SBCard>

      <SBCard className="home-card">
        <h3>Sent invites</h3>
        <ErrorText>{error}</ErrorText>
        {isLoading ? (
          <LoadingView />
        ) : invites.length === 0 ? (
          <p className="home-hint">No invites yet.</p>
        ) : (
          invites.map((invite) => (
            <div key={invite.id} className="event-row league-management-row" style={{ cursor: "default" }}>
              <div className="event-row-main">
                <strong>{invite.inviteeEmail}</strong>
                <span>
                  {INVITE_ROLE_LABELS[invite.role]} · {inviteTargetDisplayName(invite)} · code {invite.code}
                </span>
              </div>
              <div className="event-row-badges league-row-actions">
                <span className={`badge${invite.status === "accepted" ? " badge-published" : ""}`}>{invite.status}</span>
                {invite.status === "sent" && (
                  <>
                    <SecondaryButton title="Resend" onClick={() => void act(() => resendInvite(invite.id))} />
                    <SecondaryButton title="Revoke" destructive onClick={() => void act(() => revokeInvite(invite.id))} />
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </SBCard>
    </div>
  );
}
